import { ARButton, useARContext } from "@animech/ar";
import {
	Button,
	ButtonProps,
	ModalPortal,
	ToggleButton,
	Tooltip,
	TooltipPortal,
	TooltipPortalProps,
	useRespondTo,
	useRespondToRange,
} from "@animech/ui";
import {
	FunctionComponent,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import * as Styled from "./viewport-panel.style";
import {
	useUiData,
	MobileMenuPanel,
	rightPanelWidth,
	theme,
	SaveIcon,
	CustomIconButton,
	CameraResetIcon,
	OpenIcon,
	CartButton,
	useCartPricing,
	RoomButton,
	RoomPanel,
	RoomPopover,
} from "src/ui";
import { Term, root, isRoundTable, SIDE_INDICES } from "@definitions";
import { useRepositionKey } from "src/ui/hooks";
import { ApiDataContext } from "src/api";
import { SendEmailModal, OpenDesignModal } from "src/ui/components/modals";
import {
	EmailIcon,
	GlobeIcon,
	WizardIcon,
	ListIcon,
	RestartIcon,
} from "src/ui/components/icons";
import { SaveModal } from "src/ui/components/modals/save-modal/save-modal";
import { OutgoingMessageEventType } from "src/postmessage-handler";
import { BuildInfo } from "src/utility";
import { isDarkFloorStyle } from "src/scene/room";
import { generatedRoomEnabled } from "src/scene/room/room-enabled";

const isDevMode = BuildInfo.mode === "development";

export const ViewportPanel: FunctionComponent = () => {
	const { renderUiSlot, visualization, localizer, configuratorBridge, room } =
		useUiData();
	const {
		leftSidebarOpen,
		onToggleBottombar,
		designModalOpened,
		saveModalOpened,
		onToggleOpenDesignModal,
		onToggleSaveModal,
	} = visualization;
	const { formattedTotalPrice, priceSummaryDescription } = useCartPricing();

	const closeOpenDesignModal = useCallback(
		() => onToggleOpenDesignModal(false),
		[onToggleOpenDesignModal],
	);
	const closeSaveModal = useCallback(
		() => onToggleSaveModal(false),
		[onToggleSaveModal],
	);

	const tableType = configuratorBridge.getValueSync(root.tableType);
	const tableName = localizer.localizePropertyValue(root.tableType, tableType);

	const chairName = isRoundTable(tableType)
		? localizer.localizePropertyValue(
				root.chair[SIDE_INDICES.round].chairType,
				configuratorBridge.getValueSync(
					root.chair[SIDE_INDICES.round].chairType,
				),
			)
		: [
				...new Set([
					localizer.localizePropertyValue(
						root.chair[SIDE_INDICES.top].chairType,
						configuratorBridge.getValueSync(
							root.chair[SIDE_INDICES.top].chairType,
						),
					),
					localizer.localizePropertyValue(
						root.chair[SIDE_INDICES.bottom].chairType,
						configuratorBridge.getValueSync(
							root.chair[SIDE_INDICES.bottom].chairType,
						),
					),
					localizer.localizePropertyValue(
						root.chair[SIDE_INDICES.left].chairType,
						configuratorBridge.getValueSync(
							root.chair[SIDE_INDICES.left].chairType,
						),
					),
					localizer.localizePropertyValue(
						root.chair[SIDE_INDICES.right].chairType,
						configuratorBridge.getValueSync(
							root.chair[SIDE_INDICES.right].chairType,
						),
					),
				]),
			].join(", ");

	const [roomPanelOpen, setRoomPanelOpen] = useState(false);
	const toggleRoomPanel = useCallback(
		() => setRoomPanelOpen((open) => !open),
		[],
	);
	const closeRoomPanel = useCallback(() => setRoomPanelOpen(false), []);

	const productInfoContent = renderUiSlot("productInfo");

	const isTabletUp = useRespondToRange("tablet", "up");
	const isMobileBreakpoint = useRespondTo("mobile");
	const isMobile = isMobileBreakpoint && !window.electron;

	return (
		<Styled.Root>
			<ModalPortal
				open={designModalOpened}
				onShouldClose={closeOpenDesignModal}
			>
				<OpenDesignModal />
			</ModalPortal>
			<ModalPortal open={saveModalOpened} onShouldClose={closeSaveModal}>
				<SaveModal />
			</ModalPortal>
			<Styled.DynamicViewport
				data-panel-open={isMobile && leftSidebarOpen ? "" : undefined}
			>
				{/* Dynamic viewport that resizes based on bottom/side panels */}
				<Styled.Position position="top left">
					{isMobile && <MobileMenuPanel />}
					{isTabletUp && (
						<Styled.Control>
							<TooltipWrapper term="ui-buttons:toggle-bottombar">
								<ToggleButton
									value={leftSidebarOpen}
									onValueChange={onToggleBottombar}
									color="neutral"
									appearance="solid"
									itemIcons={["_chevron_right", "_chevron_left"]}
									rootAttributes={{ "data-shape": "circle" }}
								/>
							</TooltipWrapper>
						</Styled.Control>
					)}
				</Styled.Position>

				{productInfoContent && isTabletUp && (
					<Styled.Position position="top center">
						<Styled.ProductInfo>{productInfoContent}</Styled.ProductInfo>
					</Styled.Position>
				)}

				<Styled.Position position="bottom left">
					<Styled.AppTitle overDarkFloor={isDarkFloorStyle(room.floorStyle)}>
						{localizer.localizeTerm("viewportAppTitle")}
					</Styled.AppTitle>
				</Styled.Position>

				{isTabletUp && generatedRoomEnabled && (
					<Styled.Position position="bottom right">
						<RoomPopover open={roomPanelOpen} onClose={closeRoomPanel}>
							<TooltipWrapper
								term="ui-buttons:room"
								open={roomPanelOpen ? false : undefined}
							>
								<RoomButton open={roomPanelOpen} onClick={toggleRoomPanel} />
							</TooltipWrapper>
						</RoomPopover>
					</Styled.Position>
				)}

				{!isTabletUp && (
					<>
						<MobileTopToolBar />
						<MobileBottomToolBar
							roomPanelOpen={roomPanelOpen}
							onToggleRoomPanel={toggleRoomPanel}
						/>
						{roomPanelOpen && (
							<RoomPanel variant="mobile" onClose={closeRoomPanel} />
						)}
					</>
				)}
			</Styled.DynamicViewport>
			<Styled.StaticViewport leftSidebarOpen={leftSidebarOpen}>
				{/* Static viewport that fills the entire screen */}
				{isTabletUp && (
					<Styled.Position position="top right">
						<ToolBar />
						<Styled.CartButtonAndInfoWrapper>
							<CartButton
								size="small"
								css={{
									height: 43,
									width: `calc(${rightPanelWidth[0]} - ${theme.app.viewport.padding})`,
								}}
							/>
							<Styled.PriceContainer>
								<Styled.TotalPrice>
									{localizer.localizeTerm("totalPrice")} {formattedTotalPrice}
								</Styled.TotalPrice>
								<Styled.PriceDisclaimer>
									{priceSummaryDescription}
								</Styled.PriceDisclaimer>
							</Styled.PriceContainer>
						</Styled.CartButtonAndInfoWrapper>
					</Styled.Position>
				)}
			</Styled.StaticViewport>
		</Styled.Root>
	);
};

const TooltipWrapper = ({
	term,
	children,
	...tooltipPortalProps
}: Omit<TooltipPortalProps, "children" | "anchorElement"> & {
	term: Term;
	children: JSX.Element;
}) => {
	const { localizer } = useUiData();
	const wrapperRef = useRef<HTMLDivElement>(null);
	// react-popper's popperUpdate() is stale after mount, so layout shifts
	// (e.g. price text changing width) move the anchor but the tooltip stays
	// put. Changing the key forces TooltipPortal to remount with a fresh
	// Popper instance that reads the correct anchor position.
	const repositionKey = useRepositionKey(
		wrapperRef,
		tooltipPortalProps.open !== undefined,
	);

	return (
		<div ref={wrapperRef} style={{ display: "contents" }}>
			<TooltipPortal
				{...tooltipPortalProps}
				anchorElement={children}
				toggleEvent="hover"
			>
				<Tooltip key={repositionKey}>{localizer.localizeTerm(term)}</Tooltip>
			</TooltipPortal>
		</div>
	);
};

const ToolBar = () => {
	const {
		visualization,
		modals,
		cart,
		menu: { onRestart, onExportToGLTFCallback },
		emitPostMessage,
	} = useUiData();
	const { onOpen: openSummary } = cart;
	const { onToggleOpenDesignModal, onToggleSaveModal, onOpenLanguageModal } =
		visualization;
	const { articles, exportData } = useContext(ApiDataContext);
	const { designId, name } = exportData;
	const arContext = useARContext(false);

	const openOpenEmailModal = () => {
		modals.show(<SendEmailModal />).done();
	};

	const noProductsExist = !articles.length;

	return (
		<Styled.Toolbar>
			<Styled.ControlGroup>
				<Styled.Control
					style={
						!isDevMode
							? { display: "none" /* temporarily hidden */ }
							: undefined
					}
				>
					<TooltipWrapper term="ui-buttons:wizard">
						<CustomIconButton
							onClick={() =>
								emitPostMessage(OutgoingMessageEventType.SHOW_WIZARD)
							}
						>
							<WizardIcon />
						</CustomIconButton>
					</TooltipWrapper>
				</Styled.Control>
				<Styled.Control
					style={
						!isDevMode
							? { display: "none" /* temporarily hidden */ }
							: undefined
					}
				>
					<TooltipWrapper term="ui-buttons:globe">
						<CustomIconButton onClick={onOpenLanguageModal}>
							<GlobeIcon />
						</CustomIconButton>
					</TooltipWrapper>
				</Styled.Control>
				{arContext && (
					<Styled.Control>
						<TooltipWrapper term="ui-buttons:ar">
							<ARButton
								color="neutral"
								appearance="solid"
								icon="ar-2"
								disabled={noProductsExist}
							/>
						</TooltipWrapper>
					</Styled.Control>
				)}
				<Styled.Control>
					<TooltipWrapper term="ui-buttons:export3d">
						<Button
							onClick={onExportToGLTFCallback}
							color="neutral"
							appearance="solid"
							icon="document-download"
							disabled={noProductsExist}
						/>
					</TooltipWrapper>
				</Styled.Control>
				<UndoRedoControls />
				<ResetCameraFocusControl />
				<Styled.Control>
					<TooltipWrapper term="ui-buttons:save">
						<CustomIconButton
							onClick={() => onToggleSaveModal(true)}
							disabled={noProductsExist}
						>
							<SaveIcon />
						</CustomIconButton>
					</TooltipWrapper>
				</Styled.Control>
				<Styled.Control>
					<TooltipWrapper term="ui-buttons:open">
						<CustomIconButton onClick={() => onToggleOpenDesignModal(true)}>
							<OpenIcon />
						</CustomIconButton>
					</TooltipWrapper>
				</Styled.Control>
				<Styled.Control>
					<TooltipWrapper term="ui-buttons:restart">
						<CustomIconButton onClick={onRestart}>
							<RestartIcon />
						</CustomIconButton>
					</TooltipWrapper>
				</Styled.Control>
				<Styled.Control>
					<TooltipWrapper term="ui-buttons:email">
						<CustomIconButton
							onClick={openOpenEmailModal}
							disabled={noProductsExist}
						>
							<EmailIcon />
						</CustomIconButton>
					</TooltipWrapper>
				</Styled.Control>
				<Styled.Control>
					<TooltipWrapper term="ui-buttons:article-list">
						<CustomIconButton onClick={openSummary}>
							<ListIcon />
						</CustomIconButton>
					</TooltipWrapper>
				</Styled.Control>
			</Styled.ControlGroup>
			{designId && (
				<Styled.DesignInfo>
					<Styled.DesignName>{name}</Styled.DesignName>
					<Styled.DesigId>{`( ${designId} )`}</Styled.DesigId>
				</Styled.DesignInfo>
			)}
		</Styled.Toolbar>
	);
};

const MobileTopToolBar = () => {
	const { localizer } = useUiData();
	const { formattedTotalPrice } = useCartPricing();
	return (
		<Styled.Position position="top right">
			<Styled.Toolbar transparentButtons={true}>
				<Styled.ControlGroup>
					<UndoRedoControls />
					{/* <Styled.Control>
						<Button
							onClick={menu.onExit}
							color="neutral"
							appearance="solid"
							icon="close"
						/>
					</Styled.Control> */}
				</Styled.ControlGroup>
			</Styled.Toolbar>
			<Styled.TotalPrice>
				<span>{localizer.localizeTerm("totalPrice")}</span>
				<span>{formattedTotalPrice}</span>
			</Styled.TotalPrice>
		</Styled.Position>
	);
};

const MobileBottomToolBar = ({
	roomPanelOpen,
	onToggleRoomPanel,
}: {
	roomPanelOpen: boolean;
	onToggleRoomPanel(): void;
}) => {
	const {
		visualization: { select, leftSidebarOpen, onToggleBottombar },
		cart,
		localizer,
	} = useUiData();
	const arContext = useARContext(false);
	const { onOpen: openSummary } = cart;

	return (
		<Styled.Position position="bottom right">
			<Styled.Toolbar>
				<Styled.ControlGroup>
					{generatedRoomEnabled && (
						<Styled.Control data-open={roomPanelOpen}>
							<Button
								onClick={onToggleRoomPanel}
								color="neutral"
								appearance="solid"
								icon="home"
								rounded
								rootAttributes={{
									"aria-expanded": roomPanelOpen,
									"aria-label": localizer.localizeTerm("ui-buttons:room"),
								}}
							/>
						</Styled.Control>
					)}
					{arContext && (
						<Styled.Control onClick={() => select()}>
							<ARButton color="neutral" appearance="solid" rounded />
						</Styled.Control>
					)}

					<ResetCameraFocusControl />
					<Styled.Control>
						<Button
							onClick={openSummary}
							icon="cart"
							color="neutral"
							appearance="solid"
						></Button>
					</Styled.Control>
					<Styled.Control>
						<ToggleButton
							value={leftSidebarOpen}
							onValueChange={onToggleBottombar}
							color="neutral"
							appearance="solid"
							itemIcons={["_chevron_up", "_chevron_down"]}
							rootAttributes={{
								"data-shape": "circle",
							}}
						/>
					</Styled.Control>
				</Styled.ControlGroup>
			</Styled.Toolbar>
		</Styled.Position>
	);
};

const UndoRedoControls = () => {
	const {
		history: { canRedo, canUndo, onRedo, onUndo },
	} = useUiData();

	return (
		<>
			<Styled.Control>
				<TooltipWrapper term="ui-buttons:undo">
					<Button
						onClick={onUndo}
						disabled={!canUndo()}
						color="neutral"
						appearance="solid"
						icon="_undo"
					/>
				</TooltipWrapper>
			</Styled.Control>
			<Styled.Control>
				<TooltipWrapper term="ui-buttons:redo">
					<Button
						onClick={onRedo}
						disabled={!canRedo()}
						color="neutral"
						appearance="solid"
						icon="_redo"
					/>
				</TooltipWrapper>
			</Styled.Control>
		</>
	);
};

const ResetCameraFocusControl = (buttonProps: ButtonProps) => {
	const {
		visualization: { onResetCameraFocus },
	} = useUiData();

	return (
		<Styled.Control>
			<TooltipWrapper term="ui-buttons:reset-camera" open={true}>
				<CustomIconButton
					onClick={() => onResetCameraFocus(false)}
					{...buttonProps}
				>
					<CameraResetIcon />
				</CustomIconButton>
			</TooltipWrapper>
		</Styled.Control>
	);
};
